import { useEffect, useState } from "react";
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useSession } from "@clerk/clerk-react";
import { Spinner } from "@heroui/react";
import { FileText, Files, ArrowLeft } from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useNavigate } from "react-router-dom";
import { ChevronRight, ArrowUpRight } from "assets/icons";

const SHAREPOINT_FOLDER_ID = "01V4TSQ4LKM3Y5OKHCUZEYIK3GCCULDZMT";
const SHAREPOINT_FOLDER_URL = "https://xyzinvestbv.sharepoint.com/sites/XYZinvest/Gedeelde%20documenten/23.%20Yallyhub/Documenten%20dashboard";

export const Draaiboeken = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const { session } = useSession();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        const response = await fetchWithAuth(
          `${API_URL}/graph/folder/${SHAREPOINT_FOLDER_ID}/files`,
          { method: "GET" },
          session,
        );
        const data = await response.json();
        setDocuments(data);
      } catch (error) {
        console.error("Error fetching documents:", error);
      } finally {
        setLoading(false);
      }
    };

    if (session) fetchDocuments();
  }, [session]);

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Draaiboeken &mdash; Yally</title>
        </Helmet>
      </HelmetProvider>
      
      <div className="z-10 pt-6 mb-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="p-2 hover:bg-neutral-primary/5 rounded-lg transition-colors"
            >
              <ArrowLeft size={24} weight="regular" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-neutral-primary flex items-center gap-2">
                <FileText weight="duotone" size={28} />
                Draaiboeken
              </h1>
              <p className="text-sm text-neutral-secondary-mid mt-1">
                Alle draaiboeken en procedures
              </p>
            </div>
          </div>
          
          <a
            href={SHAREPOINT_FOLDER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-moss-primary hover:bg-moss-primary/10 rounded-lg transition-colors"
          >
            Open map in SharePoint <ArrowUpRight className="h-[18px] w-[18px]" />
          </a>
        </div>

        {/* Documents List */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Spinner size="lg" color="success" />
          </div>
        ) : documents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-neutral-secondary-mid">
            <Files size={64} weight="duotone" className="mb-4 text-neutral-secondary-mid/50" />
            <h3 className="text-xl font-semibold mb-2">Geen draaiboeken gevonden</h3>
            <p className="text-sm">Er zijn momenteel geen draaiboeken beschikbaar</p>
          </div>
        ) : (
          <div className="relative overflow-hidden rounded-lg bg-white border border-neutral-primary/20 shadow-sm">
            <div className="divide-y divide-neutral-primary/20">
              {documents.map((doc: any) => (
                <div
                  key={doc.id}
                  onClick={() => window.open(doc.url, "_blank")}
                  className="group flex w-full cursor-pointer items-center justify-between p-4 hover:bg-neutral-primary/5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <FileText weight="duotone" size={24} className="text-moss-primary" />
                    <div>
                      <p className="font-medium text-neutral-primary">{doc.name}</p>
                      {doc.lastModifiedDateTime && (
                        <p className="text-xs text-neutral-secondary-mid mt-0.5">
                          Laatst gewijzigd: {new Date(doc.lastModifiedDateTime).toLocaleDateString('nl-NL', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="h-[20px] w-[20px] text-moss-primary duration-250 group-hover:translate-x-1" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Draaiboeken;

